import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { SearchResult, findMatches } from '@/lib/search-utils';
import { addRecentSearch, getRecentSearches, RecentSearch } from '@/lib/recent-searches';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import ContactSearchDetail from './search/ContactSearchDetail';
import DealSearchDetail from './search/DealSearchDetail';
import NoteSearchDetail from './search/NoteSearchDetail';
import RecentSearchesPanel from './RecentSearchesPanel';
import { cn } from '@/lib/utils';
import { AppEmptyState } from '@/components/ui/app-empty-state';
import { Briefcase, FileText, User } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';

const NOTES_PREVIEW_CAP = 28;
const DEALS_PREVIEW_CAP = 32;
const CONTACTS_PREVIEW_CAP = 32;

const LEFT_RAIL_CLASS =
  'shrink-0 w-[22rem] min-w-[18rem] max-w-[min(22rem,40vw)] border-r border-border';

function getResultPath(result: SearchResult): string {
  if (result.type === 'contact') return `/contacts/${result.id}`;
  if (result.type === 'deal') return `/crm/deals/${result.id}`;
  const dealId = (result as { dealId?: string }).dealId;
  return dealId ? `/crm/deals/${dealId}` : '/crm';
}

function absoluteUrl(path: string): string {
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* ignore */
  }
}

export default function GlobalSearchPanel({
  results,
  query,
  isLoading,
  leaveSearch,
}: {
  results: SearchResult[];
  query: string;
  isLoading: boolean;
  /** Navigáció a keresőből (Header: flushSync + replace, sq cleanup kihagyás). */
  leaveSearch: (path: string) => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>(getRecentSearches());
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [dealsExpanded, setDealsExpanded] = useState(false);
  const [contactsExpanded, setContactsExpanded] = useState(false);

  const navigateToResult = useCallback(
    (result: SearchResult) => {
      if (result.type === 'contact') {
        leaveSearch(`/contacts/${result.id}`);
      } else if (result.type === 'deal') {
        leaveSearch(`/crm/deals/${result.id}`);
      } else if (result.type === 'note') {
        const dealId = (result as { dealId?: string }).dealId;
        leaveSearch(dealId ? `/crm/deals/${dealId}` : '/crm');
      }
    },
    [leaveSearch],
  );

  useEffect(() => {
    setNotesExpanded(false);
    setDealsExpanded(false);
    setContactsExpanded(false);
  }, [query]);

  useEffect(() => {
    if (isMobile) return;
    if (selectedResult === null && results.length > 0) {
      setSelectedResult(results[0]);
    } else if (
      selectedResult &&
      results.length > 0 &&
      !results.some((r) => r.id === selectedResult.id && r.type === selectedResult.type)
    ) {
      setSelectedResult(results[0]);
    }
  }, [results, selectedResult, isMobile]);

  useEffect(() => {
    const handleClear = () => {
      setRecentSearches([]);
      setSelectedResult(null);
    };
    window.addEventListener('recentSearchesCleared', handleClear);
    return () => window.removeEventListener('recentSearchesCleared', handleClear);
  }, []);

  const handleSelectSearchResult = (result: SearchResult) => {
    addRecentSearch(result);
    setRecentSearches(getRecentSearches());
    if (isMobile) {
      navigateToResult(result);
      return;
    }
    setSelectedResult(result);
  };

  const handleSelectRecentSearch = (search: RecentSearch) => {
    const result = results.find((r) => r.id === search.id && r.type === search.type);
    const target =
      result ??
      ({
        id: search.id,
        type: search.type,
        name: search.type === 'contact' ? search.title : '',
        email: search.type === 'contact' ? search.subtitle : '',
        title: search.type === 'deal' ? search.title : '',
        contactName: search.type === 'deal' ? search.subtitle : '',
        body: search.type === 'note' ? search.title : '',
        dealTitle: search.type === 'note' ? search.subtitle : '',
      } as SearchResult);

    if (isMobile) {
      navigateToResult(target);
      return;
    }
    setSelectedResult(target);
  };

  useEffect(() => {
    if (isMobile || query.trim()) return;
    if (selectedResult === null && recentSearches.length > 0) {
      const first = recentSearches[0];
      const reconstructed: SearchResult = {
        id: first.id,
        type: first.type,
        name: first.type === 'contact' ? first.title : '',
        email: first.type === 'contact' ? first.subtitle : '',
        title: first.type === 'deal' ? first.title : '',
        contactName: first.type === 'deal' ? first.subtitle : '',
        body: first.type === 'note' ? first.title : '',
        dealTitle: first.type === 'note' ? first.subtitle : '',
      } as SearchResult;
      setSelectedResult(reconstructed);
    }
  }, [recentSearches, isMobile, query, selectedResult]);

  const grouped = useMemo(
    () => ({
      deals: results.filter((r) => r.type === 'deal'),
      contacts: results.filter((r) => r.type === 'contact'),
      notes: results.filter((r) => r.type === 'note'),
    }),
    [results],
  );

  const displayedNotes = useMemo(
    () => (notesExpanded ? grouped.notes : grouped.notes.slice(0, NOTES_PREVIEW_CAP)),
    [grouped.notes, notesExpanded],
  );

  const displayedDeals = useMemo(
    () => (dealsExpanded ? grouped.deals : grouped.deals.slice(0, DEALS_PREVIEW_CAP)),
    [grouped.deals, dealsExpanded],
  );
  const displayedContacts = useMemo(
    () =>
      contactsExpanded ? grouped.contacts : grouped.contacts.slice(0, CONTACTS_PREVIEW_CAP),
    [grouped.contacts, contactsExpanded],
  );

  const flatResults = useMemo(
    () => [...displayedDeals, ...displayedContacts, ...displayedNotes],
    [displayedDeals, displayedContacts, displayedNotes],
  );

  useEffect(() => {
    if (!selectedResult || isMobile) return;
    const el = document.querySelector(
      `[data-search-row="${selectedResult.type}:${selectedResult.id}"]`,
    );
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedResult, isMobile]);

  useEffect(() => {
    if (isMobile || !query.trim() || flatResults.length === 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('textarea, select, [contenteditable="true"]')) return;
      const input = t?.closest?.('input') as HTMLInputElement | null;
      if (input && !['radio', 'checkbox', 'hidden'].includes(input.type)) {
        if (input.id !== 'global-search-input') return;
      }
      const idx = flatResults.findIndex(
        (r) => r.id === selectedResult?.id && r.type === selectedResult?.type,
      );
      if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedResult) navigateToResult(selectedResult);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const base = idx < 0 ? -1 : idx;
        const next = Math.min(flatResults.length - 1, base + 1);
        setSelectedResult(flatResults[next]);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const base = idx < 0 ? 0 : idx;
        const next = Math.max(0, base - 1);
        setSelectedResult(flatResults[next]);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile, query, flatResults, selectedResult, navigateToResult]);

  if (isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center" aria-busy="true">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">{t('common.searching')}</p>
        </div>
        <span className="sr-only" aria-live="polite">
          {t('common.searching')}
        </span>
      </div>
    );
  }

  if (!query.trim()) {
    return (
      <div className={cn('h-full min-h-0 flex gap-0', isMobile && 'flex-col')}>
        <div
          className={cn(
            'bg-background overflow-y-auto min-h-0',
            isMobile ? 'w-full flex-1' : LEFT_RAIL_CLASS,
          )}
        >
          <RecentSearchesPanel
            searches={recentSearches}
            onSelectSearch={handleSelectRecentSearch}
            selectedResult={selectedResult}
          />
        </div>
        {!isMobile && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-visible border-l border-border/50 bg-muted/10 p-1.5 sm:p-3 dark:bg-muted/5">
            {selectedResult?.type === 'contact' && (
              <div className="flex min-h-0 flex-1 flex-col overflow-visible rounded-lg border border-border/60 bg-background shadow-sm">
                <ContactSearchDetail
                  contact={selectedResult as never}
                  onNavigate={(id) => leaveSearch(`/contacts/${id}`)}
                />
              </div>
            )}
            {selectedResult?.type === 'deal' && (
              <div className="flex min-h-0 flex-1 flex-col overflow-visible rounded-lg border border-border/60 bg-background shadow-sm">
                <DealSearchDetail
                  deal={selectedResult as never}
                  onOpenDeal={() => leaveSearch(`/crm/deals/${selectedResult.id}`)}
                />
              </div>
            )}
            {selectedResult?.type === 'note' && (
              <div className="flex min-h-0 flex-1 flex-col overflow-visible rounded-lg border border-border/60 bg-background shadow-sm">
                <NoteSearchDetail
                  note={selectedResult as never}
                  onNavigate={(dealId) => leaveSearch(`/crm/deals/${dealId}`)}
                />
              </div>
            )}
            {!selectedResult && (
              <div className="flex-1 flex items-center justify-center text-muted-foreground px-6 text-center text-sm">
                {t('search.selectOrTypePrompt')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="h-full flex items-center justify-center px-4">
        <AppEmptyState title={t('search.noResultsTitle')} description={t('search.tryDifferentQuery')} />
      </div>
    );
  }

  const sectionHeaderClass = cn(
    'bg-muted/40 sticky top-0 z-10 border-b border-border',
    isMobile ? 'px-3 py-2' : 'px-4 py-2.5',
  );
  const sectionTitleClass =
    'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground';

  return (
    <div className="h-full min-h-0 flex gap-0">
      <div
        className={cn(
          'overflow-y-auto bg-background min-h-0 outline-none',
          isMobile ? 'w-full flex-1' : LEFT_RAIL_CLASS,
        )}
        tabIndex={isMobile ? undefined : 0}
        role={isMobile ? undefined : 'listbox'}
        aria-label={t('header.searchDialogTitle')}
        aria-activedescendant={
          !isMobile && selectedResult
            ? `search-opt-${selectedResult.type}-${selectedResult.id}`
            : undefined
        }
      >
        <div className="p-0" aria-live="polite">
          {isMobile && results.length > 0 && (
            <div className="px-3 py-2 text-xs font-medium text-foreground border-b border-border">
              {t('search.resultsCount', { count: results.length })}
            </div>
          )}
          {!isMobile && (
            <div className="sr-only">
              {t('search.resultsCount', { count: results.length })}
            </div>
          )}

          {grouped.deals.length > 0 && (
            <div>
              <div className={sectionHeaderClass}>
                <h3 className={sectionTitleClass}>
                  {t('search.dealsSection')} ({grouped.deals.length})
                </h3>
              </div>
              <div className="divide-y divide-border">
                {displayedDeals.map((result) => (
                  <ResultListItem
                    key={`${result.type}-${result.id}`}
                    result={result}
                    selected={
                      !isMobile &&
                      selectedResult?.id === result.id &&
                      selectedResult?.type === result.type
                    }
                    onSelect={handleSelectSearchResult}
                    query={query}
                    compact={isMobile}
                    onCopyLink={() => copyToClipboard(absoluteUrl(getResultPath(result)))}
                    onOpenNewTab={() =>
                      window.open(absoluteUrl(getResultPath(result)), '_blank', 'noopener,noreferrer')
                    }
                    t={t}
                  />
                ))}
              </div>
              {grouped.deals.length > DEALS_PREVIEW_CAP && !dealsExpanded && (
                <div className="border-t border-border p-2">
                  <button
                    type="button"
                    className="w-full rounded-md py-2 text-xs font-medium text-primary transition-colors hover:bg-muted/80"
                    onClick={() => setDealsExpanded(true)}
                  >
                    {t('search.showAllDeals', { count: grouped.deals.length })}
                  </button>
                </div>
              )}
            </div>
          )}

          {grouped.contacts.length > 0 && (
            <div>
              <div className={sectionHeaderClass}>
                <h3 className={sectionTitleClass}>
                  {t('search.contactsSection')} ({grouped.contacts.length})
                </h3>
              </div>
              <div className="divide-y divide-border">
                {displayedContacts.map((result) => (
                  <ResultListItem
                    key={`${result.type}-${result.id}`}
                    result={result}
                    selected={
                      !isMobile &&
                      selectedResult?.id === result.id &&
                      selectedResult?.type === result.type
                    }
                    onSelect={handleSelectSearchResult}
                    query={query}
                    compact={isMobile}
                    onCopyLink={() => copyToClipboard(absoluteUrl(getResultPath(result)))}
                    onOpenNewTab={() =>
                      window.open(absoluteUrl(getResultPath(result)), '_blank', 'noopener,noreferrer')
                    }
                    onCopyEmail={
                      result.type === 'contact' && result.email
                        ? () => copyToClipboard(result.email)
                        : undefined
                    }
                    t={t}
                  />
                ))}
              </div>
              {grouped.contacts.length > CONTACTS_PREVIEW_CAP && !contactsExpanded && (
                <div className="border-t border-border p-2">
                  <button
                    type="button"
                    className="w-full rounded-md py-2 text-xs font-medium text-primary transition-colors hover:bg-muted/80"
                    onClick={() => setContactsExpanded(true)}
                  >
                    {t('search.showAllContacts', { count: grouped.contacts.length })}
                  </button>
                </div>
              )}
            </div>
          )}

          {grouped.notes.length > 0 && (
            <div>
              <div className={sectionHeaderClass}>
                <h3 className={sectionTitleClass}>
                  {t('search.notesSection')} ({grouped.notes.length})
                </h3>
              </div>
              <div className="divide-y divide-border">
                {displayedNotes.map((result) => (
                  <ResultListItem
                    key={`${result.type}-${result.id}`}
                    result={result}
                    selected={
                      !isMobile &&
                      selectedResult?.id === result.id &&
                      selectedResult?.type === result.type
                    }
                    onSelect={handleSelectSearchResult}
                    query={query}
                    compact={isMobile}
                    onCopyLink={() => copyToClipboard(absoluteUrl(getResultPath(result)))}
                    onOpenNewTab={() =>
                      window.open(absoluteUrl(getResultPath(result)), '_blank', 'noopener,noreferrer')
                    }
                    t={t}
                  />
                ))}
              </div>
              {grouped.notes.length > NOTES_PREVIEW_CAP && !notesExpanded && (
                <div className="p-2 border-t border-border">
                  <button
                    type="button"
                    className="w-full rounded-md py-2 text-xs font-medium text-primary hover:bg-muted/80 transition-colors"
                    onClick={() => setNotesExpanded(true)}
                  >
                    {t('search.showAllNotes', { count: grouped.notes.length })}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!isMobile && (
        <div className="flex min-h-0 flex-1 flex-col overflow-visible border-l border-border/50 bg-muted/10 p-1.5 sm:p-3 dark:bg-muted/5">
          {selectedResult?.type === 'contact' && (
            <div className="flex min-h-0 flex-1 flex-col overflow-visible rounded-lg border border-border/60 bg-background shadow-sm">
              <ContactSearchDetail
                contact={selectedResult as never}
                onNavigate={(id) => leaveSearch(`/contacts/${id}`)}
              />
            </div>
          )}
          {selectedResult?.type === 'deal' && (
            <div className="flex min-h-0 flex-1 flex-col overflow-visible rounded-lg border border-border/60 bg-background shadow-sm">
              <DealSearchDetail
                deal={selectedResult as never}
                onOpenDeal={() => leaveSearch(`/crm/deals/${selectedResult.id}`)}
              />
            </div>
          )}
          {selectedResult?.type === 'note' && (
            <div className="flex min-h-0 flex-1 flex-col overflow-visible rounded-lg border border-border/60 bg-background shadow-sm">
              <NoteSearchDetail
                note={selectedResult as never}
                onNavigate={(dealId) => leaveSearch(`/crm/deals/${dealId}`)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultListItem({
  result,
  selected,
  onSelect,
  query,
  compact = false,
  onCopyLink,
  onOpenNewTab,
  onCopyEmail,
  t,
}: {
  result: SearchResult;
  selected: boolean;
  onSelect: (result: SearchResult) => void;
  query: string;
  compact?: boolean;
  onCopyLink: () => void;
  onOpenNewTab: () => void;
  onCopyEmail?: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const rowClass = compact
    ? `group w-full text-left px-3 py-2.5 transition-colors duration-150 flex gap-2 items-start ${
        selected ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-muted/50 border-l-2 border-l-transparent'
      }`
    : `group w-full text-left px-4 py-4 transition-colors duration-150 flex gap-3 items-start ${
        selected ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-muted/50 border-l-2 border-l-transparent'
      }`;
  const titleClass = compact
    ? 'font-medium text-xs text-foreground leading-snug min-w-0 flex-1'
    : 'font-medium text-sm text-foreground leading-snug min-w-0 flex-1';
  const subClass = compact ? 'text-[11px] text-muted-foreground mt-0.5' : 'text-xs text-muted-foreground mt-1';

  const iconWrap = compact ? 'mt-0.5 shrink-0' : 'mt-0.5 shrink-0';
  const icon =
    result.type === 'deal' ? (
      <Briefcase className={cn('text-muted-foreground', compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
    ) : result.type === 'contact' ? (
      <User className={cn('text-muted-foreground', compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
    ) : (
      <FileText className={cn('text-muted-foreground', compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
    );

  const optionId = `search-opt-${result.type}-${result.id}`;

  const inner = (
    <>
      <span className={iconWrap}>{icon}</span>
      <span className="min-w-0 flex-1 text-left">
        {result.type === 'deal' && (
          <>
            <div className={titleClass}>{highlightText(result.title, query)}</div>
            <div className={cn(subClass, 'flex flex-wrap items-center gap-1.5')}>
              {result.stageName && (
                <span className="px-1.5 py-0.5 rounded-md bg-muted text-[11px] text-foreground/90">
                  {result.stageName}
                </span>
              )}
              {result.contactName && (
                <span className="text-muted-foreground/80">{result.contactName}</span>
              )}
            </div>
          </>
        )}
        {result.type === 'contact' && (
          <>
            <div className={titleClass}>{highlightText(result.name, query)}</div>
            <div className={subClass}>{highlightText(result.email, query)}</div>
          </>
        )}
        {result.type === 'note' && (
          <>
            <div
              className={
                compact
                  ? 'text-xs text-foreground line-clamp-2 leading-snug'
                  : 'text-sm text-foreground line-clamp-2 leading-relaxed'
              }
            >
              {highlightText(
                (() => {
                  const body = result.body || '';
                  const preview = body.substring(0, 80).trim();
                  return preview + (body.length > 80 ? '...' : '');
                })(),
                query,
              )}
            </div>
            <div className={cn(subClass, 'flex flex-wrap items-center gap-1.5')}>
              {result.dealTitle && (
                <span className="text-muted-foreground/80">
                  {t('search.noteRelatedDeal')}: {result.dealTitle}
                </span>
              )}
              {result.contactName && (
                <span className="text-muted-foreground/80">· {result.contactName}</span>
              )}
            </div>
          </>
        )}
      </span>
    </>
  );

  if (compact) {
    return (
      <button
        type="button"
        data-search-row={`${result.type}:${result.id}`}
        onClick={() => onSelect(result)}
        className={rowClass}
      >
        {inner}
      </button>
    );
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <button
          type="button"
          id={optionId}
          role="option"
          aria-selected={selected}
          data-search-row={`${result.type}:${result.id}`}
          onClick={() => onSelect(result)}
          className={rowClass}
        >
          {inner}
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem onSelect={onCopyLink}>{t('search.copyPageLink')}</ContextMenuItem>
        <ContextMenuItem onSelect={onOpenNewTab}>{t('search.openInNewTab')}</ContextMenuItem>
        {onCopyEmail && (
          <ContextMenuItem onSelect={onCopyEmail}>{t('search.copyEmail')}</ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

function highlightText(text: string | null | undefined, query: string): ReactNode {
  if (!text) {
    return '';
  }

  if (!query.trim()) {
    return text;
  }

  const matches = findMatches(text, query);
  if (matches.length === 0) {
    return text;
  }

  const parts: ReactNode[] = [];
  let lastIndex = 0;

  matches.forEach((match, idx) => {
    if (match.start > lastIndex) {
      parts.push(text.substring(lastIndex, match.start));
    }
    parts.push(
      <mark key={idx} className="bg-primary/20 text-primary font-semibold rounded px-0.5">
        {text.substring(match.start, match.start + match.length)}
      </mark>,
    );
    lastIndex = match.start + match.length;
  });

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}
